
import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Wallet, User, LogOut, Settings, 
  Menu, X, Briefcase, ShieldCheck, Users as UsersIcon, BarChart2, TrendingUp 
} from 'lucide-react';
import { AffiliateDashboard } from './components/AffiliateDashboard';
import { AdminDashboard } from './components/AdminDashboard';
import { WalletView } from './components/WalletView';
import { LandingPage } from './components/LandingPage';
import { UserManagement } from './components/UserManagement';
import { SettingsView } from './components/SettingsView';
import { AffiliateReports } from './components/AffiliateReports';
import { AdminFinance } from './components/AdminFinance';
import { DebugDB } from './components/DebugDB';
import { Role } from './types';
import { SignalProvider, useSignalData } from './context/SignalContext';

const DashboardLayout: React.FC = () => {
  const { currentUser, logout, isLoading } = useSignalData();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div></div>;
  if (!currentUser) return <Navigate to="/" replace />;

  const SidebarItem = ({ to, icon: Icon, label }: { to: string, icon: any, label: string }) => {
    const isActive = location.pathname === to;
    return (
      <Link to={to} onClick={() => setIsMobileMenuOpen(false)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}>
        <Icon className={`w-5 h-5 ${isActive ? 'text-brand-600' : 'text-slate-400'}`} />
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="h-full flex flex-col">
          <div className="h-16 flex items-center px-6 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center text-white font-bold">S</div>
              <span className="font-bold text-slate-800 text-lg">SignalViet</span>
            </div>
          </div>
          <div className="flex-1 px-4 py-6 space-y-1">
            <SidebarItem to="/dashboard" icon={LayoutDashboard} label="Tổng quan" />
            {currentUser.role === Role.AFFILIATE && (
              <>
                <SidebarItem to="/reports" icon={BarChart2} label="Thống kê hiệu quả" />
                <SidebarItem to="/income" icon={Wallet} label="Thu nhập" />
              </>
            )}
            {currentUser.role === Role.ADMIN && (
               <>
                <SidebarItem to="/finance" icon={TrendingUp} label="Quản lý tài chính" />
                <SidebarItem to="/users" icon={UsersIcon} label="Quản lý thành viên" />
                <SidebarItem to="/settings" icon={Settings} label="Cấu hình hệ thống" />
               </>
            )}
          </div>
          <div className="p-4 border-t border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-3 mb-4">
              <img src={currentUser.avatar} className="w-10 h-10 rounded-full border border-slate-200" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800 truncate">{currentUser.name}</p>
                <p className="text-[10px] text-slate-500 font-mono italic">TV: {currentUser.tradingViewId}</p>
              </div>
            </div>
            <button onClick={logout} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-white transition-colors">
              <LogOut className="w-3 h-3" /> Đăng xuất
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 z-40">
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg">{isMobileMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}</button>
          <div className="flex items-center gap-4 ml-auto">
             <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                <ShieldCheck className="w-3 h-3 text-green-600" /> {currentUser.role}
             </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-50"><div className="max-w-7xl mx-auto"><Outlet /></div></main>
      </div>
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)}></div>}
    </div>
  );
};

const AppRoutes: React.FC = () => {
    const { currentUser } = useSignalData();
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/db" element={<DebugDB />} />
            <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={currentUser?.role === Role.ADMIN ? <AdminDashboard /> : <AffiliateDashboard />} />
                {currentUser?.role === Role.AFFILIATE && (
                    <>
                        <Route path="/income" element={<WalletView />} />
                        <Route path="/reports" element={<AffiliateReports />} />
                    </>
                )}
                {currentUser?.role === Role.ADMIN && (
                    <>
                        <Route path="/finance" element={<AdminFinance />} />
                        <Route path="/users" element={<UserManagement />} />
                        <Route path="/settings" element={<SettingsView />} />
                    </>
                )}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
        </Routes>
    );
};

const App: React.FC = () => <SignalProvider><AppRoutes /></SignalProvider>;
export default App;
